// Codex texts (from GDD 03 Buildings and GDD 04 Military) and effect labels (spec 2026-10-06 §3.3).
import type { CodexResponse, FacilityType, ShipType } from '@stars-and-ships/api-types';
import { formatNumber } from './labels';

export const BUILDING_TEXT: Partial<Record<FacilityType, string>> = {
  command_center:
    'The heart of the system. Its level is the highest level any other building here can reach.',
  metal_mine: 'Extracts Metal, the main building material for structures and hulls.',
  gas_harvester: 'Collects Gas, a building and ship material.',
  supply_farm: 'Produces Supplies, a building and ship material.',
  metal_storage:
    'Holds Metal. Production stops when it is full, and a share of the stock stays safe from raiders.',
  gas_storage:
    'Holds Gas. Production stops when it is full, and a share of the stock stays safe from raiders.',
  supply_storage:
    'Holds Supplies. Production stops when it is full, and a share of the stock stays safe from raiders.',
  trading_port:
    'Trades resources for Credits, the currency for claiming systems. Higher levels trade larger amounts at once.',
  space_dock: 'Builds ships. Higher levels unlock bigger classes and build faster.',
  habitat:
    'Ship production capacity. Each ship uses Habitat points by size while it exists; there must be room before a ship can be built.',
  odp: 'Fires on attackers in every battle round over this system. It cannot be destroyed.',
  warp_gate:
    'Fleets that leave this system through the warp-gate network fly faster the higher its level is.',
  diplomatic_embassy: 'Generates Influence and sends scouts to gather intel on other systems.',
};

/** Facilities whose numbers depend on the system's planets. */
export const PLANET_DEPENDENT = new Set<FacilityType>([
  'metal_mine',
  'gas_harvester',
  'supply_farm',
  'diplomatic_embassy',
]);

export const SHIP_TEXT: Partial<Record<ShipType, string>> = {
  interceptor: 'Small, fast and cheap strike fighter. Good for raids and early defence.',
  transport: 'Unarmed hauler with a cargo hold. It carries raid loot and takes no part in combat.',
  frigate: 'The workhorse of the front line: tougher and better armed than an Interceptor.',
  cruiser: 'Heavy mid-line warship with thick armour. Strong against Interceptors and Frigates.',
};

type EffectKey = CodexResponse['buildings'][number]['levels'][number]['effects'][number]['key'];
const RESOURCE_OF: Partial<Record<FacilityType, string>> = {
  metal_mine: 'Metal',
  gas_harvester: 'Gas',
  supply_farm: 'Supplies',
  metal_storage: 'Metal',
  gas_storage: 'Gas',
  supply_storage: 'Supplies',
};

export function effectLabel(key: EffectKey, facility: FacilityType): string {
  switch (key) {
    case 'max_building_level':
      return 'Max level of other buildings';
    case 'production_per_h':
      return `${RESOURCE_OF[facility] ?? 'Resource'} per hour`;
    case 'storage_cap':
      return `${RESOURCE_OF[facility] ?? 'Resource'} capacity`;
    case 'max_resources_per_trade':
      return 'Max resources per trade';
    case 'ship_build_time_pct':
      return 'Ship build time';
    case 'habitat_capacity_points':
      return 'Habitat points';
    case 'odp_attack':
      return 'Attack per round';
    case 'gate_speed_pct':
      return 'Gate speed';
    case 'influence_per_h':
      return 'Influence per hour';
  }
}

/** At most one decimal: rates turn fractional at odd galaxy speeds. */
export const formatRate = (value: number): string => formatNumber(Math.round(value * 10) / 10);

export function effectValue(key: EffectKey, value: number): string {
  return key === 'ship_build_time_pct' || key === 'gate_speed_pct'
    ? `${formatRate(value)}%`
    : formatRate(value);
}
